import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Check, Loader2, Lock, Star, Compass, Share2, X } from 'lucide-react';
import { toast } from 'sonner';
import { DIAGNOSTIC_QUESTIONS, SPHERE_LABELS, calculateDiagnosticScore, hasQuiz } from '@/utils/diagnosticQuestions';
import { ResultInterpretation } from '@/components/diagnostic/ResultInterpretation';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import AuthModal from '@/components/AuthModal';
import API_BASE_URL from '@/utils/apiUrl';
import axios from 'axios';
import Footer from '@/components/Footer';

export default function DiagnosticGamePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const baseNumber = location.state?.baseNumber;
  const birthDate = location.state?.birthDate;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedResult, setSavedResult] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showAbortDialog, setShowAbortDialog] = useState(false);

  const advanceTimer = useRef(null);
  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  // Guard: no base number
  if (!baseNumber) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#2d1b4e]/60 border-[#d4af37]/30 p-8 text-center max-w-md">
          <Lock className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
          <p className="text-foreground/80 mb-4">Grįžkite į pradžią ir įveskite gimimo datą.</p>
          <Button onClick={() => navigate('/')} className="bg-[#d4af37] text-black font-bold">
            Grįžti
          </Button>
        </Card>
      </div>
    );
  }

  // Numbers without a quiz: Coming Soon
  if (!hasQuiz(baseNumber)) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#2d1b4e]/60 border-[#d4af37]/30 p-8 text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#d4af37]/20 border-2 border-[#d4af37]/50 mb-4 mx-auto">
            <span className="text-3xl font-bold text-[#d4af37]">{baseNumber}</span>
          </div>
          <h2 className="text-xl font-display text-[#d4af37] mb-3">Diagnostika ruošiama</h2>
          <p className="text-foreground/70 text-sm mb-6">
            Skaičiaus {baseNumber} diagnostinis žaidimas bus prieinamas netrukus. Šiuo metu tobulinama skaičiaus 1 versija.
          </p>
          <Button onClick={() => navigate('/')} className="bg-[#d4af37] text-black font-bold">
            Grįžti
          </Button>
        </Card>
      </div>
    );
  }

  const questions = DIAGNOSTIC_QUESTIONS[baseNumber];
  const currentQ = questions[currentIndex];
  const totalQ = questions.length;
  const selectedAnswer = answers[currentQ.id];
  const allAnswered = Object.keys(answers).length === totalQ;

  const handleSelect = (letter) => {
    setAnswers((prev) => ({ ...prev, [currentQ.id]: letter }));
    // Auto-advance to the next question (except on the last one)
    clearTimeout(advanceTimer.current);
    if (currentIndex < totalQ - 1) {
      advanceTimer.current = setTimeout(() => setCurrentIndex((i) => Math.min(i + 1, totalQ - 1)), 250);
    }
  };

  const handlePrev = () => {
    clearTimeout(advanceTimer.current);
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  };

  const handleFinish = async () => {
    if (!allAnswered) {
      toast.error('Atsakykite į visus klausimus');
      return;
    }
    const score = calculateDiagnosticScore(answers, totalQ);

    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/diagnostic/save`, {
        base_number: baseNumber,
        birth_date: birthDate || '',
        answers,
        score_a: score.a,
        score_b: score.b,
        score_c: score.c,
        percent_a: score.aPercent,
        percent_b: score.bPercent,
        percent_c: score.cPercent,
        user_email: user?.email || null,
      });
      setSavedResult({ ...score, id: res.data?.result?.id });
    } catch {
      // Still show result even if save fails
      setSavedResult(score);
    }
    setSaving(false);
    setShowResult(true);
  };

  const handleShare = async () => {
    const shareUrl = window.location.origin;
    const shareText = `Atlikau Numenaxis gyvenimo situacijos ir balanso diagnostiką! Mano bazinis skaičius – ${baseNumber}. Sužinok savąjį:`;
    const shareData = { title: 'Numenaxis diagnostika', text: shareText, url: shareUrl };
    const copyFallback = async () => {
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        toast.success('Nuoroda nukopijuota į iškarpinę');
      } catch {
        toast.error('Nepavyko pasidalinti');
      }
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err?.name !== 'AbortError') await copyFallback();
      }
    } else {
      await copyFallback();
    }
  };

  const handleTryAnotherDate = () => navigate('/', { state: { scrollToDiagnostic: true } });

  const handleAbortTest = () => setShowAbortDialog(true);

  const confirmAbort = () => {
    setShowAbortDialog(false);
    clearTimeout(advanceTimer.current);
    setAnswers({});
    setCurrentIndex(0);
    setShowResult(false);
    setSavedResult(null);
    navigate('/', { state: { scrollToDiagnostic: true } });
  };

  // ─── RESULT SCREEN ───
  if (showResult && savedResult) {
    // WINNER LOGIC: "Kryžkelė" (tie) triggers ONLY when all three counts are
    // perfectly equal (e.g. 4-4-4). Otherwise the highest count wins; a two-way
    // top tie (e.g. 2-5-5) is broken by percentage, then by fixed order.
    const { a, b, c, aPercent, bPercent, cPercent } = savedResult;
    let resultType;
    if (a === b && b === c) {
      resultType = 'tie';
    } else {
      resultType = [
        { key: 'a', count: a, pct: aPercent },
        { key: 'b', count: b, pct: bPercent },
        { key: 'c', count: c, pct: cPercent },
      ].sort((x, y) => y.count - x.count || y.pct - x.pct)[0].key;
    }

    return (
      <div className="min-h-screen pb-16">
        <div className="container mx-auto px-4 py-8 max-w-lg">
          <Card className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#2d1b4e]/60 border-[#d4af37]/30 p-6 sm:p-8" data-testid="diagnostic-result-screen">
            <h2 className="font-display text-2xl text-center text-[#d4af37] mb-6">
              Diagnostikos rezultatai
            </h2>

            <div className="text-center mb-6">
              <span className="text-sm text-muted-foreground">Bazinis skaičius</span>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#d4af37]/20 border-2 border-[#d4af37]/50 mx-auto mt-1">
                <span className="text-2xl font-bold text-[#d4af37]">{baseNumber}</span>
              </div>
            </div>

            {/* Score Bars */}
            <div className="space-y-4 mb-8">
              <ScoreBar label="A — Aktyvus / Pozityvus" percent={savedResult.aPercent} count={savedResult.a} color="emerald" />
              <ScoreBar label="B — Atvirkštinis / Pragmatinis" percent={savedResult.bPercent} count={savedResult.b} color="amber" />
              <ScoreBar label="C — Situacinis / Lankstus" percent={savedResult.cPercent} count={savedResult.c} color="purple" />
            </div>

            <div className="text-center text-xs text-muted-foreground mb-6">
              Iš viso atsakymų: {savedResult.a + savedResult.b + savedResult.c} / {totalQ}
            </div>

            {/* ── Conditional Interpretation Text ── */}
            <ResultInterpretation baseNumber={baseNumber} resultType={resultType} onTryAnotherDate={handleTryAnotherDate} />

            {/* ── Navigation Options ── */}
            <div className="space-y-3 mt-6">
              <Button
                onClick={() => setShowAuth(true)}
                className="w-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold py-3"
                data-testid="diagnostic-signup-btn"
              >
                <Star className="w-4 h-4 mr-2" />
                Užsiregistruoti ir išsaugoti duomenis
              </Button>
              <Button
                variant="outline"
                onClick={handleShare}
                className="w-full border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 py-3"
                data-testid="diagnostic-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Pasidalinti rezultatu
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/10 py-3"
                data-testid="diagnostic-back-home"
              >
                <Compass className="w-4 h-4 mr-2" />
                Grįžti į Pagrindinį Meniu
              </Button>
            </div>
          </Card>
        </div>
        <Footer />
        {showAuth && <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} defaultTab="register" />}
      </div>
    );
  }

  // ─── QUIZ SCREEN ───
  const progress = ((currentIndex + 1) / totalQ) * 100;

  return (
    <div className="min-h-screen pb-16">
      <div className="container mx-auto px-4 py-8 max-w-lg">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="grid grid-cols-3 items-center mb-3">
            <span className="text-xs text-muted-foreground justify-self-start">{currentIndex + 1} / {totalQ}</span>
            <span className="justify-self-center px-4 py-1.5 rounded-full bg-[#d4af37]/15 text-[#d4af37] text-lg sm:text-xl font-bold border border-[#d4af37]/40 leading-none" data-testid="quiz-base-number">
              Sk. {baseNumber}
            </span>
            <span className="justify-self-end px-2 py-0.5 rounded-full bg-[#d4af37]/15 text-[#d4af37] text-xs font-medium">
              {SPHERE_LABELS[currentQ.sphere]}
            </span>
          </div>
          <div className="h-1.5 bg-[#0f0f23]/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#2d1b4e]/60 border-[#d4af37]/20 p-6 sm:p-8 mb-6" data-testid="diagnostic-question-card">
          <p className="text-foreground text-base sm:text-lg leading-relaxed mb-6 text-center" data-testid="question-text">
            {currentQ.question}
          </p>

          {/* Choice Buttons */}
          <div className="space-y-3">
            {['a', 'b', 'c'].map((letter) => {
              const isSelected = selectedAnswer === letter;
              return (
                <button
                  key={letter}
                  onClick={() => handleSelect(letter)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-[#d4af37] bg-[#d4af37]/15 text-foreground'
                      : 'border-[#d4af37]/15 bg-[#0f0f23]/40 text-foreground/80 hover:border-[#d4af37]/40 hover:bg-[#d4af37]/5'
                  }`}
                  data-testid={`choice-${letter}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      isSelected
                        ? 'border-[#d4af37] bg-[#d4af37]'
                        : 'border-[#d4af37]/40'
                    }`}>
                      {isSelected && <Check className="w-4 h-4 text-black" />}
                      {!isSelected && <span className="text-xs text-[#d4af37]/60 font-bold uppercase">{letter}</span>}
                    </div>
                    <span className="text-sm leading-relaxed">{currentQ.choices[letter]}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/10 disabled:opacity-30"
            data-testid="quiz-prev"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Atgal
          </Button>

          {currentIndex === totalQ - 1 && (
            <Button
              onClick={handleFinish}
              disabled={!allAnswered || saving}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold disabled:opacity-30"
              data-testid="quiz-finish"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Užbaigti
            </Button>
          )}
        </div>

        {/* Abort / Exit test */}
        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={handleAbortTest}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70 hover:text-[#d4af37] transition-colors duration-200"
            data-testid="quiz-abort"
          >
            <X className="w-3.5 h-3.5" />
            Nutraukti testą
          </button>
        </div>

        {/* Quick answer dots */}
        <div className="flex items-center justify-center gap-1.5 mt-6">
          {questions.map((q, i) => (
            <button
              key={q.id}
              onClick={() => setCurrentIndex(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i === currentIndex
                  ? 'bg-[#d4af37] scale-125'
                  : answers[q.id]
                    ? 'bg-[#d4af37]/50'
                    : 'bg-[#d4af37]/15'
              }`}
              data-testid={`dot-${i}`}
            />
          ))}
        </div>
      </div>

      {/* Abort confirmation modal */}
      <AlertDialog open={showAbortDialog} onOpenChange={setShowAbortDialog}>
        <AlertDialogContent
          className="bg-gradient-to-br from-[#1a1a3e] to-[#2d1b4e] border-[#d4af37]/30 text-foreground max-w-sm"
          data-testid="abort-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#d4af37]">Nutraukti testą?</AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/70">
              Ar tikrai norite nutraukti testą? Jūsų atsakymai nebus išsaugoti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#d4af37]"
              data-testid="abort-cancel"
            >
              Tęsti testą
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAbort}
              className="bg-red-500/90 hover:bg-red-500 text-white"
              data-testid="abort-confirm"
            >
              Nutraukti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}

// ─── Score Bar Component ───
function ScoreBar({ label, percent, count, color }) {
  const colorMap = {
    emerald: 'from-emerald-500 to-emerald-400',
    amber: 'from-amber-500 to-amber-400',
    purple: 'from-purple-500 to-purple-400',
  };
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-foreground/80">{label}</span>
        <span className="text-[#d4af37] font-mono font-bold">{percent}%</span>
      </div>
      <div className="h-3 bg-[#0f0f23]/60 rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${colorMap[color]} rounded-full transition-all duration-500`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{count} atsakymai</p>
    </div>
  );
}

// ─── Result Interpretation moved to components/diagnostic/ResultInterpretation.jsx ───
